import type { DateAuPlusTot, DossierVu } from './api/types';
import {
  A_TOI,
  auPlusTotLe,
  ON_ATTEND,
  RENDEZ_VOUS,
  reponsesDuMonde,
  RIEN_A_FAIRE_ICI,
  TUILE_RIEN_DEDANS,
  TUILE_TOUT_EST_FAIT,
} from './vocabulaire';

/**
 * Les règles de la page Projets, sans rendu (chantier `revue-design`, ticket 05, maquette
 * `design/projets.html` ; l'étagère depuis le chantier etagere-des-projets) : les trois tris et la
 * jauge d'un carnet. Les mots sont dans `vocabulaire.ts`.
 */

export type TriDesProjets = 'echeance' | 'recents' | 'a_z';

const alphabet = new Intl.Collator('fr-FR', { sensitivity: 'base' });
const parTitre = (a: DossierVu, b: DossierVu) => alphabet.compare(a.titre, b.titre);

/** Une date d'abord, puis ce qui attend un délai (« pas encore calculable »), puis « pas de date ». */
function rangDeLaDate(date: DateAuPlusTot): number {
  if (date.sorte === 'date') return 0;
  return date.sorte === 'pas_calculable' ? 1 : 2;
}

/**
 * Les projets rangés, sans toucher à la liste reçue.
 * - « Par échéance » : la fin au plus tôt la plus proche d'abord ; puis ce qui n'est pas encore
 *   calculable, puis ce qui n'a pas de date ; à égalité, de A à Z.
 * - « Récents » : le dernier changement le plus proche d'abord ; sans changement connu, à la fin.
 * - « A à Z » : à la française, sans tenir compte des majuscules ni des accents.
 */
export function rangerLesProjets(liste: readonly DossierVu[], tri: TriDesProjets): DossierVu[] {
  const copie = [...liste];
  switch (tri) {
    case 'echeance':
      return copie.sort((a, b) => {
        const rang = rangDeLaDate(a.dateAuPlusTot) - rangDeLaDate(b.dateAuPlusTot);
        if (rang !== 0) return rang;
        if (a.dateAuPlusTot.sorte === 'date' && b.dateAuPlusTot.sorte === 'date') {
          const jour = a.dateAuPlusTot.jour.localeCompare(b.dateAuPlusTot.jour);
          if (jour !== 0) return jour;
        }
        return parTitre(a, b);
      });
    case 'recents':
      return copie.sort((a, b) => {
        if (a.dernierChangement === b.dernierChangement) return parTitre(a, b);
        if (!a.dernierChangement) return 1;
        if (!b.dernierChangement) return -1;
        return b.dernierChangement.localeCompare(a.dernierChangement);
      });
    case 'a_z':
      return copie.sort(parTitre);
  }
}

/*
 * ── Tous les projets, au téléphone (chantier `telephone-projets-fiche`, ticket 01) ──
 * Maquette `design/telephone.html`, piste « Projets » ; décisions d'Igor du 2026-10-05
 * (revue 12 et 20, cadrage 13).
 */

/** Prioritaire d'abord, puis le reste de « Maintenant », puis sous l'eau, puis rien. */
const RANG_DE_LA_PLACE: Record<NonNullable<DossierVu['prochaine']>['place'], number> = {
  prioritaire: 0,
  pendant_ce_temps: 1,
  sous_l_eau: 2,
};

/**
 * Les tuiles rangées selon ce qui presse, sans toucher à la liste reçue :
 * 1. le plus de bulles en « Prioritaire » d'abord (ce qui doit se tenir dans les 3 jours) ;
 * 2. puis la place de sa prochaine chose dans « Maintenant » (Prioritaire, Pendant ce temps,
 *    sous l'eau), un projet sans prochaine chose après ;
 * 3. à égalité, l'ordre « Par échéance » de l'ordinateur (fin au plus tôt, puis A à Z).
 */
export function rangerSelonCeQuiPresse(liste: readonly DossierVu[]): DossierVu[] {
  const parEcheance = rangerLesProjets(liste, 'echeance');
  const rangEcheance = new Map(parEcheance.map((d, i) => [d.id, i]));
  const place = (d: DossierVu) => (d.prochaine ? RANG_DE_LA_PLACE[d.prochaine.place] : 3);
  return [...liste].sort(
    (a, b) =>
      b.faits.prioritaires - a.faits.prioritaires ||
      place(a) - place(b) ||
      (rangEcheance.get(a.id) ?? 0) - (rangEcheance.get(b.id) ?? 0)
  );
}

/**
 * La part du cercle qui se remplit, de 0 à 1 : ce qui est fait sur le total. Elle recule quand
 * on ajoute des tâches (Igor l'accepte en le sachant, cadrage 13). Elle ne s'écrit jamais en
 * pourcentage : l'écran dit « X sur Y ».
 */
export function partDuCercle(faites: number, total: number): number {
  if (total <= 0) return 0;
  return Math.min(1, Math.max(0, faites / total));
}

/** Ce que dit l'encart d'une tuile. `monde` : sur l'ambre, ce qui attend le monde. */
export interface EncartDeLaTuile {
  ton: 'toi' | 'monde' | 'calme';
  etiquette: string | null;
  phrase: string;
  precision: string | null;
}

/**
 * L'encart d'une tuile : la prochaine chose de « Maintenant », « À toi ». Sans elle, ce qu'on
 * attend, sur l'ambre : la réponse qui n'est jamais revenue, sinon combien de réponses du monde
 * et la fin au plus tôt quand elle est connue. Sans rien à attendre non plus, une phrase calme.
 * Jamais « retard » (principe 3).
 */
export function encartDeLaTuile(dossier: DossierVu): EncartDeLaTuile {
  if (dossier.prochaine) {
    return { ton: 'toi', etiquette: A_TOI, phrase: dossier.prochaine.titre, precision: null };
  }
  const { relances, premiereRelance, enAttente, prochainRendezVous } = dossier.faits;
  const date = dossier.dateAuPlusTot;
  const plusTot = date.sorte === 'date' ? auPlusTotLe(date.jour) : null;
  if (relances > 0 && premiereRelance) {
    return { ton: 'monde', etiquette: ON_ATTEND, phrase: `${premiereRelance.titre} n’est jamais revenu`, precision: plusTot };
  }
  if (enAttente > 0) {
    return { ton: 'monde', etiquette: ON_ATTEND, phrase: reponsesDuMonde(enAttente), precision: plusTot };
  }
  if (prochainRendezVous) {
    return { ton: 'toi', etiquette: RENDEZ_VOUS, phrase: prochainRendezVous.titre, precision: null };
  }
  const { faites, total } = dossier.progression;
  if (total === 0) return { ton: 'calme', etiquette: null, phrase: TUILE_RIEN_DEDANS, precision: null };
  if (faites >= total) return { ton: 'calme', etiquette: null, phrase: TUILE_TOUT_EST_FAIT, precision: null };
  return { ton: 'calme', etiquette: null, phrase: RIEN_A_FAIRE_ICI, precision: null };
}

/** La part de la jauge d'un carnet : ce qui est fait, jamais tout à fait vide. Jamais écrite. */
export function partFaite(faites: number, total: number): number {
  if (total <= 0) return 0.02;
  return Math.min(1, Math.max(0.02, faites / total));
}
